import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, Tooltip, CircularProgress,
  TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import Swal from 'sweetalert2';
import AsignacionFormModal from './AsignacionFormModal';
import { useAuth } from '../context/AuthContext';

import { ASIGNACIONES_API } from '../apiConfig';
const API_BASE_URL = ASIGNACIONES_API;


export default function AsignacionesManager({ espacios = [] }) {
  const [asignaciones, setAsignaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAsig, setSelectedAsig] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filterEstado, setFilterEstado] = useState('');
  const [filterCreatedBy, setFilterCreatedBy] = useState('');
  const [filterCodigo, setFilterCodigo] = useState('');
  const [users, setUsers] = useState([]);
  const LIMIT = 50;

  const fetchAsignaciones = async (isLoadMore = false) => {
    if (isLoadMore) setLoadingMore(true);
    else setLoading(true);

    try {
      const nextPage = isLoadMore ? page + 1 : 1;
      let url = `${API_BASE_URL}?page=${nextPage}&limit=${LIMIT}`;
      if (filterEstado) url += `&estado=${filterEstado}`;
      if (filterCreatedBy) url += `&created_by=${filterCreatedBy}`;
      if (filterCodigo) url += `&codigo_espacio=${filterCodigo}`;
      if (searchTerm) url += `&cliente=${searchTerm}`;
      
      const { data } = await axios.get(url);
      
      if (isLoadMore) {
        setAsignaciones(prev => [...prev, ...data]);
        setPage(nextPage);
      } else {
        setAsignaciones(data);
        setPage(1);
      }

      setHasMore(data.length === LIMIT);
    } catch (error) {
      console.error('Error fetching asignaciones:', error);
      Swal.fire('Error', 'No se pudieron cargar los contratos', 'error');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchAsignaciones();
    if (isAdmin && users.length === 0) {
      axios.get(`${API_BASE_URL.replace('/asignaciones', '/admin/users')}`)
        .then(res => setUsers(res.data))
        .catch(err => console.error("Error fetching users:", err));
    }
  }, [filterEstado, filterCreatedBy, filterCodigo, searchTerm]);

  const handleEdit = (asig) => {
    setSelectedAsig(asig);
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: '¿Eliminar contrato?',
      text: "Esta acción no se puede deshacer",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${API_BASE_URL}/${id}`);
        Swal.fire('¡Eliminado!', 'El contrato ha sido borrado.', 'success');
        fetchAsignaciones();
      } catch (error) {
        Swal.fire('Error', 'Hubo un problema al eliminar', 'error');
      }
    }
  };

  const getEspacioLabel = (codigo) => {
    const esp = espacios.find(e => e.Codigo === codigo);
    if (!esp) return codigo;
    return `${codigo} - ${esp.Formato || esp.Tipo_Espacio || ''}`;
  };

  const getUserEmail = (uid) => {
    if (!uid) return 'N/A';
    const user = users.find(u => u.uid === uid);
    return user ? user.email : uid;
  };

  const formatPrice = (price) => {
    if (!price) return 'N/A';
    return new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(price);
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'Activo': return 'success';
      case 'Programado': return 'info';
      case 'Finalizado': return 'default';
      case 'Cancelado': return 'error';
      default: return 'primary';
    }
  };

  return (
    <Box sx={{ p: 4, height: '100%', overflowY: 'auto', bgcolor: 'background.default' }}>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight={800} color="primary" sx={{ letterSpacing: '-0.03em' }}>
              Contratos y Asignaciones
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Administra los periodos de ocupación y ventas de tus vallas publicitarias.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => { setSelectedAsig(null); setModalOpen(true); }}
            >
              Nuevo Contrato
            </Button>
            <Button variant="outlined" onClick={() => fetchAsignaciones()} disabled={loading}>Actualizar</Button>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Buscar por cliente..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            }}
            sx={{ bgcolor: 'white', borderRadius: 1, minWidth: 200 }}
          />

          <TextField
            size="small"
            placeholder="Código de valla..."
            value={filterCodigo}
            onChange={(e) => setFilterCodigo(e.target.value)}
            sx={{ bgcolor: 'white', borderRadius: 1, minWidth: 150 }}
          />
          
          <FormControl size="small" sx={{ minWidth: 150, bgcolor: 'white' }}>
            <InputLabel>Estado</InputLabel>
            <Select
              value={filterEstado}
              label="Estado"
              onChange={(e) => setFilterEstado(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="Activo">Activo</MenuItem>
              <MenuItem value="Programado">Programado</MenuItem>
              <MenuItem value="Finalizado">Finalizado</MenuItem>
              <MenuItem value="Cancelado">Cancelado</MenuItem>
            </Select>
          </FormControl>

          {isAdmin && (
            <FormControl size="small" sx={{ minWidth: 200, bgcolor: 'white' }}>
              <InputLabel>Creado por</InputLabel>
              <Select
                value={filterCreatedBy}
                label="Creado por"
                onChange={(e) => setFilterCreatedBy(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {users.map(u => (
                  <MenuItem key={u.uid} value={u.uid}>{u.email}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {(filterEstado || filterCreatedBy || filterCodigo || searchTerm) && (
            <Button 
              size="small" 
              onClick={() => {
                setFilterEstado('');
                setFilterCreatedBy('');
                setFilterCodigo('');
                setSearchTerm('');
              }}
            >
              Limpiar
            </Button>
          )}
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper 
          elevation={0}
          sx={{ 
            borderRadius: 4, 
            overflow: 'hidden', 
            border: '1px solid', 
            borderColor: 'divider',
            bgcolor: 'background.paper',
            boxShadow: '0 10px 30px rgba(0,0,0,0.04)'
          }}
        >
          <TableContainer sx={{ maxHeight: '70vh' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>ID</TableCell>
                  <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>Valla</TableCell>
                  <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>Cliente</TableCell>
                  <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>Periodo</TableCell>
                  <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>Monto</TableCell>
                  <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>Estado</TableCell>
                  <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>Creado por</TableCell>
                  <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {asignaciones.map((asig) => (
                  <TableRow key={asig.id} hover>
                    <TableCell>#{asig.id}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{getEspacioLabel(asig.codigo_espacio)}</TableCell>
                    <TableCell>{asig.cliente}</TableCell>
                    <TableCell variant="body2">
                      {asig.fecha_inicio} al {asig.fecha_fin}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {formatPrice(asig.monto_total)}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={asig.estado} 
                        size="small" 
                        color={getStatusColor(asig.estado)}
                        sx={{ fontWeight: 'bold' }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      {getUserEmail(asig.created_by)}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => handleEdit(asig)} color="primary">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {isAdmin && (
                        <Tooltip title="Eliminar">
                          <IconButton size="small" onClick={() => handleDelete(asig.id)} color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {hasMore && (
            <Box sx={{ p: 2, textAlign: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
              <Button 
                onClick={() => fetchAsignaciones(true)} 
                disabled={loadingMore}
                size="small"
              >
                {loadingMore ? 'Cargando...' : 'Cargar más contratos'}
              </Button>
            </Box>
          )}
        </Paper>
      )}

      {modalOpen && (
        <AsignacionFormModal 
          open={modalOpen} 
          onClose={() => setModalOpen(false)} 
          onSuccess={fetchAsignaciones}
          espacios={espacios}
          asignacion={selectedAsig}
        />
      )}
    </Box>
  );
}
